/* eslint-disable @typescript-eslint/no-unused-vars */
import {
	useEnvContext,
	useGoogleAnalyticsContext,
	useWebStoreContext
} from "contexts";
import { FeatureType } from "interfaces/feature-tracker";
import { useEffect } from "react";
import { useGetFeatureTrackerQuery } from "redux/apis";
import { useAppDispatch } from "./useAppDispatch";
import {
	onOpenPopUp,
	selectDipState,
	selectPopUpManagerState
} from "redux/slices";
import { SignUpIncentiveResponse } from "interfaces/sign-up-incentive";
import { useUpdatePopupStateMutation } from "redux/apis/signUpIncentive";
import { useAppSelector } from "./useAppSelector";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { FeaturesFlags } from "helpers";
import { useSearchParams } from "react-router-dom";
import useNewDay from "./useNewDay";
import { usePopupsState } from "./usePopupsState";

export const useLoadSignUpIncentive = () => {
	const { authToken, userTracker, syncUserTracker } = useWebStoreContext();
	//const { sidAccessToken, sidIdentityToken } = useScopelyIdentityContext();
	const dispatch = useAppDispatch();

	const {
		isAnyPopupOpen,
		hasSeenRequiredPageTutorial,
		IsFriendReferralAcceptInviteChecking
	} = usePopupsState();
	const { postUserMetadata, isLinkedScopely } = useWebStoreContext();
	const { isFeatureFlagEnabled } = useEnvContext();
	const [searchParams] = useSearchParams();
	const searchParamsInvalidate: boolean =
		searchParams.has("state") || searchParams.has("deeplinkId");
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { newDay } = useNewDay();

	const {
		data,
		refetch
	}: { data?: SignUpIncentiveResponse; refetch: () => void } =
		useGetFeatureTrackerQuery(
			{
				FeatureType: FeatureType.SIGN_UP_INCENTIVE,
				token: authToken?.value
			},
			{
				skip:
					searchParamsInvalidate ||
					!authToken?.value ||
					!userTracker ||
					!isFeatureFlagEnabled(FeaturesFlags.SCOPELY_CONNECT),
				pollingInterval: 600000
			}
		);

	const [updatePopupState, { isLoading: updatePopupStateLoading }] =
		useUpdatePopupStateMutation();

	const setPopupAsSeen = async (popupType: string) => {
		await updatePopupState({
			popupType
		})
			.unwrap()
			.then(() => {
				dispatch(onOpenPopUp("sign-up-incentive-reward"));
				reportEvent("scopely_sign_up_incentive", {
					source: "popup",
					action: "show",
					popup_type: "announcement"
				});
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "scopely_sign_up_incentive",
					event_type: "web_scopely_sign-up-incentive",
					action: "show",
					source: "popup",
					type: "announcement"
				});
			});
	};

	// useEffect(() => {
	// 	if (authToken?.value) {
	// 		refetch();
	// 	}
	// }, [authToken?.value]);

	useEffect(() => {
		if (newDay) {
			refetch();
		}
	}, [newDay]);

	// *Trigger incentive popup
	useEffect(() => {
		if (
			data?.showIncentivePopup &&
			!updatePopupStateLoading &&
			hasSeenRequiredPageTutorial &&
			!isAnyPopupOpen &&
			!IsFriendReferralAcceptInviteChecking
		) {
			setPopupAsSeen("incentive-popup-viewed");
		}
	}, [
		data?.showIncentivePopup,
		isAnyPopupOpen,
		hasSeenRequiredPageTutorial,
		location.pathname,
		IsFriendReferralAcceptInviteChecking
	]);

	// *Trigger rewards popup
	useEffect(() => {
		if (
			data?.tracker?.scopelyUserId &&
			isLinkedScopely &&
			data?.tracker?.incentiveRewardDate &&
			userTracker?.metadata?.hasDisplayedSignUpIncentiveRewards !== "true"
		) {
			const userMetaData = {
				key: "hasDisplayedSignUpIncentiveRewards",
				value: "true"
			};
			postUserMetadata(userMetaData).then(() => {
				dispatch(onOpenPopUp("sign-up-incentive-reward"));
				syncUserTracker();
			});
			reportEvent("scopely_sign_up_incentive", {
				source: "popup",
				action: "show",
				popup_type: "reward"
			});
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "scopely_sign_up_incentive",
				event_type: "web_scopely_sign-up-incentive",
				action: "show",
				source: "popup",
				type: "reward"
			});
		}
	}, [data?.tracker?.scopelyUserId, isLinkedScopely]);
};
